"use client";

import { type Dispatch, useCallback, useMemo, useRef, useState } from "react";
import { addDays } from "@/features/event-details/_logic/date-utils";
import { getNightsBetween } from "@/features/event-details-rooms/_logic/rooms";
import { MAX_SHOULDER_NIGHTS } from "../_logic/constants";
import { buildStayNights, deriveShoulderCounts, dropNight } from "../_logic/guestroom-nights";
import type {
  RoomTypeCounts,
  ShoulderNightActions,
  ShoulderNightCounts,
  ShoulderSegment,
  StayNight,
  WizardAction,
} from "../_types/wizard-types";

const NO_SHOULDERS: ShoulderNightCounts = { pre: 0, post: 0 };

interface UseShoulderNightsArgs {
  /** Program check-in (`YYYY-MM-DD`), the first peak night. */
  checkIn: string | null;
  /** Program check-out (`YYYY-MM-DD`), the day after the last peak night. */
  checkOut: string | null;
  /** Current per-night breakdown. */
  detailed: { [date: string]: RoomTypeCounts } | null;
  /** Wizard dispatch — removing a night saves the breakdown without that night's counts. */
  dispatch: Dispatch<WizardAction>;
}

/**
 * Owns the guestrooms grid's pre/post nights: how many sit on each side of the program, the rows
 * they produce, and the add/remove actions. The program range (check-in/check-out) never changes
 * here — pre/post nights only add keys to the per-night breakdown outside it.
 *
 * The counts start from whatever the saved breakdown already holds outside the program range, so
 * a resumed draft shows its pre/post rows again. When the program range itself changes, the
 * counts reset (the reducer clears the breakdown on a range change too).
 *
 * @param args - Program check-in/check-out, the current breakdown, and the wizard dispatch.
 * @returns The grid's rows (`stayNights`) and the pre/post controls (`shoulders`).
 */
export function useShoulderNights({ checkIn, checkOut, detailed, dispatch }: UseShoulderNightsArgs): {
  stayNights: StayNight[];
  shoulders: ShoulderNightActions;
} {
  // Hooks
  const detailedRef = useRef(detailed);
  detailedRef.current = detailed;

  // States
  const rangeKey = `${checkIn ?? ""}|${checkOut ?? ""}`;
  const [stored, setStored] = useState(() => ({
    rangeKey,
    counts: deriveShoulderCounts(detailed, checkIn, checkOut),
  }));

  // Derived values
  const counts = stored.rangeKey === rangeKey ? stored.counts : NO_SHOULDERS;
  const peakNights = useMemo(
    () => (checkIn && checkOut ? getNightsBetween(checkIn, checkOut) : []),
    [checkIn, checkOut]
  );
  const stayNights = useMemo(() => buildStayNights(peakNights, counts), [peakNights, counts]);

  // Handlers
  const onAdd = useCallback(
    (segment: ShoulderSegment) => {
      setStored((prev) => {
        const base = prev.rangeKey === rangeKey ? prev.counts : NO_SHOULDERS;
        if (base[segment] >= MAX_SHOULDER_NIGHTS) return prev;
        return { rangeKey, counts: { ...base, [segment]: base[segment] + 1 } };
      });
    },
    [rangeKey]
  );

  const onRemove = useCallback(
    (segment: ShoulderSegment) => {
      if (!checkIn || !checkOut || counts[segment] === 0) return;
      const outermost = segment === "pre" ? addDays(checkIn, -counts.pre) : addDays(checkOut, counts.post - 1);
      const current = detailedRef.current;
      if (outermost && current && outermost in current) {
        dispatch({ type: "SET_ACCOMMODATIONS_DETAILED", payload: dropNight(current, outermost) });
      }
      setStored({ rangeKey, counts: { ...counts, [segment]: counts[segment] - 1 } });
    },
    [checkIn, checkOut, counts, dispatch, rangeKey]
  );

  const shoulders = useMemo<ShoulderNightActions>(
    () => ({
      // Pre/post nights sit around the program's own nights, so there's nothing to add around
      // until the program has at least one night.
      canAdd: {
        pre: peakNights.length > 0 && counts.pre < MAX_SHOULDER_NIGHTS,
        post: peakNights.length > 0 && counts.post < MAX_SHOULDER_NIGHTS,
      },
      onAdd,
      onRemove,
    }),
    [counts, onAdd, onRemove, peakNights.length]
  );

  return { stayNights, shoulders };
}
